import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSession } from "../lib/session";
import { getCompetition, boardForCompetition, competitionStandings,
         listCompetitionEntries, competitionHandicaps } from "../lib/api";
import { PageHead, Loading, Notice } from "../components/ui";
import Back from "../components/Back";
import PlayerPanel from "../components/PlayerPanel";

/**
 * The competition leaderboard.
 *
 * One board for the whole competition, with tabs for the overall standing
 * and each round — rather than a separate board buried inside every round.
 * Tapping a player opens their cards and stats, like a tour board.
 */

const METRICS = [
  { k: "nett",   label: "Nett",       pos: "pos_nett",   val: (r) => r.nett,   tot: (r) => r.total_nett },
  { k: "gross",  label: "Gross",      pos: "pos_gross",  val: (r) => r.gross,  tot: (r) => r.total_gross },
  { k: "points", label: "Stableford", pos: "pos_points", val: (r) => r.points, tot: (r) => r.total_points },
];

export default function CompetitionBoard() {
  const { id } = useParams();
  const { profile, society } = useSession();

  const [comp, setComp] = useState(null);
  const [board, setBoard] = useState([]);
  const [standings, setStandings] = useState([]);
  const [error, setError] = useState("");
  const [view, setView] = useState("overall");
  const [metric, setMetric] = useState("nett");
  const [openPlayer, setOpenPlayer] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([getCompetition(id), boardForCompetition(id), competitionStandings(id)])
      .then(([c, b, s]) => {
        setComp(c); setBoard(b); setStandings(s);
        setMetric(c.primary_metric === "stableford" ? "points" : c.primary_metric);
      })
      .catch((e) => setError(e.message));
  }, [id]);

  const rounds = useMemo(
    () => (comp?.rounds ?? []).slice().sort((a, b) => a.round_number - b.round_number),
    [comp]);

  const spec = METRICS.find((m) => m.k === metric);

  const rows = useMemo(() => {
    if (!comp) return { placed: [], noReturn: [], waiting: [] };

    if (view === "overall") {
      const key = metric === "points" ? "pos_points" : metric === "gross" ? "pos_gross" : "pos_nett";
      const placed = standings
        .filter((r) => r[key] != null)
        .sort((a, b) => a[key] - b[key])
        .map((r) => ({ ...r, _pos: r[key], _val: spec.tot(r) }));
      return { placed, noReturn: [], waiting: [] };
    }

    const inRound = board.filter((r) => r.round_id === view);
    const placed = inRound
      .filter((r) => r[spec.pos] != null)
      .sort((a, b) => a[spec.pos] - b[spec.pos])
      .map((r) => ({ ...r, _pos: r[spec.pos], _val: spec.val(r) }));
    const noReturn = inRound.filter((r) => r.card_status === "no_return" && r[spec.pos] == null);
    const waiting = inRound.filter((r) => !["submitted", "no_return"].includes(r.card_status));
    return { placed, noReturn, waiting };
  }, [view, metric, board, standings, comp, spec]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!comp) return <Loading />;
  if (!comp) return (
    <>
      <Back to="/competitions">Back</Back>
      <Notice kind="error">That competition isn't available.</Notice>
    </>
  );

  const overall = view === "overall";
  const tie = {};
  rows.placed.forEach((r) => { tie[r._pos] = (tie[r._pos] || 0) + 1; });

  return (
    <>
      <Back to={`/c/${id}/today`}>{comp?.name ?? "Competition"}</Back>

      <PageHead
        title="Leaderboard"
        sub={[
          comp.name,
          overall
            ? comp.aggregation === "best_n"
              ? `best ${comp.counting_rounds} of ${rounds.length}`
              : `all ${rounds.length} rounds`
            : null,
        ].filter(Boolean).join(" · ")}>
        <button className="btn btn-ghost" disabled={exporting || standings.length === 0}
                onClick={async () => {
                  setExporting(true);
                  try {
                    const [{ exportStandings }, entries, hcaps] = await Promise.all([
                      import("../lib/pdf"),
                      listCompetitionEntries(id),
                      competitionHandicaps(id),
                    ]);
                    await exportStandings({
                      competition: comp, society, rows: standings, entries,
                      handicaps: hcaps, rounds: rounds.length,
                      courses: [...new Set((comp.rounds ?? [])
                        .map((r) => r.courses?.name).filter(Boolean))],
                    });
                  } catch (e) {
                    setError(e.message ?? String(e));
                  } finally { setExporting(false); }
                }}>
          {exporting ? "Building…" : "Export PDF"}
        </button>
      </PageHead>

      {/* round selector */}
      <div style={{ display: "flex", gap: ".3rem", overflowX: "auto", marginBottom: ".8rem" }}>
        <button onClick={() => setView("overall")}
                style={chipStyle(overall)}>Overall</button>
        {rounds.map((r) => (
          <button key={r.id} onClick={() => setView(r.id)} style={chipStyle(view === r.id)}>
            R{r.round_number}
          </button>
        ))}
      </div>

      {/* metric tabs */}
      <div className="tabs">
        {METRICS.map((m) => (
          <button key={m.k} data-on={metric === m.k} onClick={() => setMetric(m.k)}>
            {m.label}
          </button>
        ))}
      </div>

      {rows.placed.length === 0 ? (
        <div className="empty"><p>No cards in yet.</p></div>
      ) : (
        <div className="surface" style={{ overflow: "hidden" }}>
          <table className="lb">
            <thead>
              <tr>
                <th>Pos</th>
                <th>Player</th>
                {overall ? <th>Rds</th> : <th>Hcp</th>}
                {!overall && <th className="opt">Gross</th>}
                {!overall && <th className="opt">Pts</th>}
                <th>{spec.label}</th>
              </tr>
            </thead>
            <tbody>
              {rows.placed.map((r) => {
                const pid = r.profile_id;
                const isOpen = openPlayer === pid;
                return (
                  <React.Fragment key={r.entry_id ?? pid}>
                    <tr className={`${r._pos === 1 ? "first " : ""}${pid === profile?.id ? "me " : ""}${isOpen ? "open" : ""}`}
                        onClick={() => setOpenPlayer(isOpen ? null : pid)}
                        style={{ cursor: "pointer" }}>
                      <td className="pos">{tie[r._pos] > 1 ? "T" : ""}{r._pos}</td>
                      <td className="nm">
                        <span className="caret" aria-hidden="true" />
                        {r.full_name}
                        <small>{overall
                          ? `${r.rounds_played} round${r.rounds_played === 1 ? "" : "s"} played`
                          : `${r.tee_name} tees`}</small>
                      </td>
                      {overall
                        ? <td className="dim">{r.rounds_played}</td>
                        : <td className="dim">{r.playing_handicap}</td>}
                      {!overall && <td className="dim opt">{r.gross ?? "\u2013"}</td>}
                      {!overall && <td className="dim opt">{r.points ?? "\u2013"}</td>}
                      <td className="key">{r._val ?? "\u2013"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="detail">
                        <td colSpan={overall ? 4 : 6}>
                          <PlayerPanel competitionId={id} profileId={pid} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted" style={{ fontSize: ".75rem", marginTop: ".6rem", lineHeight: 1.55 }}>
        Tap a name to open their cards and stats underneath it.
      </p>

      {rows.noReturn.length > 0 && (
        <>
          <h3 className="eyebrow" style={{ margin: "1.3rem 0 .5rem" }}>No return</h3>
          <div className="list">
            {rows.noReturn.map((r) => (
              <button key={r.entry_id} className="list-row"
                      onClick={() => setOpenPlayer(r.profile_id)}>
                <span>
                  <strong style={{ display: "block" }}>{r.full_name}</strong>
                  <small className="muted" style={{ fontSize: ".6875rem" }}>
                    gross {r.gross} on the card
                  </small>
                </span>
                <span className="pill pill-quiet">NR</span>
              </button>
            ))}
          </div>
        </>
      )}

      {rows.waiting.length > 0 && (
        <>
          <h3 className="eyebrow" style={{ margin: "1.3rem 0 .5rem" }}>
            Awaiting cards · {rows.waiting.length}
          </h3>
          <div className="list">
            {rows.waiting.map((r) => (
              <div className="list-row" key={r.entry_id}>
                <span className="muted">{r.full_name}</span>
                <span className="pill pill-quiet">
                  {r.card_status === "in_progress" ? "started" : "not in"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

const chipStyle = (on) => ({
  flex: "0 0 auto", padding: ".45rem .85rem", cursor: "pointer",
  fontSize: ".8125rem", fontWeight: 600, borderRadius: "var(--r)",
  border: `1px solid ${on ? "var(--navy)" : "var(--line)"}`,
  background: on ? "var(--navy)" : "none",
  color: on ? "var(--on-navy)" : "var(--dim)",
});
